import React, { useCallback, useEffect, useState } from "react";
import { Gauge, Loader2, RefreshCw, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ukDayTime } from "@/lib/time";
import { Card, Figure, Note, Pill } from "@/components/hub/Shell";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const TOP_UP_URL = "https://www.endole.co.uk/";

function Meter({ left, peak, low }) {
  const top = Math.max(peak || 0, left || 0, low * 2);
  const pct = Math.max(0, Math.min(100, (left / top) * 100));
  const mark = (low / top) * 100;
  const fill = left === 0 ? "#C8151F" : left < low ? "linear-gradient(90deg,#E39A00,#F5B829)" : "linear-gradient(90deg,#0B6B4F,#12B76A)";
  return (
    <div className="relative mt-3 h-2.5 rounded-full bg-slate-100" data-testid="hub-credits-meter"
         role="meter" aria-valuemin={0} aria-valuemax={top} aria-valuenow={left}>
      <i className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: fill }} />
      <span className="absolute -top-1 -bottom-1 w-px bg-slate-400" style={{ left: `${mark}%` }} title={`Warns under ${low}`} />
    </div>
  );
}

/** The master switch for all of Connexis search — on by default; off stops every search, fill and check. */
function MasterSwitch({ d, onChange }) {
  const [busy, setBusy] = useState(false);
  const on = !d.paused;
  const flip = async (next) => {
    setBusy(true);
    try {
      const r = await api.post("/hub/company-check/switch", { on: next });
      onChange(r.data);
      toast.success(next ? "Connexis search is back on" : "Connexis search is switched off", {
        action: { label: "Undo", onClick: () => flip(!next) },
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change that just now"); }
    finally { setBusy(false); }
  };
  return (
    <div className={`mb-5 flex items-start justify-between gap-4 rounded-2xl border p-3.5 transition-colors ${on ? "border-emerald-200 bg-emerald-50/60" : "border-amber-200 bg-amber-50/70"}`}
         data-testid="hub-connexis-switch-row">
      <div className="min-w-0">
        <div className="text-[13.5px] font-bold text-slate-900">Connexis search <span className={on ? "text-emerald-700" : "text-amber-700"} data-testid="hub-connexis-switch-state">· {on ? "On" : "Off"}</span></div>
        <div className="mt-0.5 text-[12px] leading-snug text-slate-600">
          {on ? "Everything ticked below is live. Untick a part to pause just that one."
            : `Switched off${d.paused_at ? ` ${ukDayTime(d.paused_at, "")}` : ""} — forms are typed by hand and no new checks run. Reports already on cases stay.`}
        </div>
      </div>
      <Switch checked={on} disabled={busy} onCheckedChange={flip} aria-label="Connexis search on or off" data-testid="hub-connexis-switch" />
    </div>
  );
}

/** Tick boxes under the master switch — each part of Connexis search on or off on its own. */
const PARTS = [
  ["autofill", "Form auto-fill", "Company search and the 'Is this you?' director buttons on the website and every portal"],
  ["reports", "Full company report on submit", "Accounts, history, CCJs and highlights pulled when a case comes in — you can still run one by hand on a case"],
  ["pdfs", "Filed accounts PDFs", "The latest filed accounts attached to the case's documents with each report"],
];

function Parts({ d, onChange }) {
  const [busy, setBusy] = useState("");
  const f = d.features || {};
  const flip = async (name, on) => {
    setBusy(name);
    try {
      const r = await api.post("/hub/company-check/features", { name, on });
      onChange(r.data);
      toast.success(`${PARTS.find((p) => p[0] === name)[1]} ${on ? "on" : "off"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change that just now"); }
    finally { setBusy(""); }
  };
  return (
    <ul className={`mb-5 -mt-2 space-y-1 rounded-2xl border border-slate-200 bg-white p-2 ${d.paused ? "opacity-55" : ""}`} data-testid="hub-connexis-parts">
      {PARTS.map(([name, label, sub]) => (
        <li key={name}>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl px-2.5 py-2 transition-colors hover:bg-slate-50">
            <Checkbox checked={f[name] !== false} disabled={d.paused || busy === name} onCheckedChange={(v) => flip(name, !!v)}
                      className="mt-0.5" data-testid={`hub-connexis-part-${name}`} />
            <span className="min-w-0">
              <span className="block text-[13px] font-semibold text-slate-900">{label}</span>
              <span className="block text-[11.5px] leading-snug text-slate-500">{sub}</span>
            </span>
          </label>
        </li>
      ))}
    </ul>
  );
}

/** Who can download the company check as a PDF — lender desks and introducers switched on or off on their own. */
const PDF_SIDES = [
  ["cc_pdf_lenders", "Lender desks can download the PDF", "The full company check as a branded Connexis PDF, filed accounts at the back"],
  ["cc_pdf_introducers", "Introducers can download the PDF", "The same PDF on their own cases — every page says who it was made for"],
];

function PdfAccess() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/hub/settings").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  if (!s) return null;
  const flip = async (key, on, label) => {
    setBusy(key);
    try {
      const r = await api.patch("/hub/settings", { [key]: on });
      setS(r.data);
      toast.success(`${label.replace(" can download the PDF", "")}: PDF ${on ? "on" : "off"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't change that just now"); }
    finally { setBusy(""); }
  };
  return (
    <div className="mb-5" data-testid="hub-cc-pdf-access">
      <div className="mb-1.5 px-1 text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">Company check PDF</div>
      <ul className="space-y-1 rounded-2xl border border-slate-200 bg-white p-2">
        {PDF_SIDES.map(([key, label, sub]) => (
          <li key={key}>
            <label className="flex cursor-pointer items-start gap-3 rounded-xl px-2.5 py-2 transition-colors hover:bg-slate-50">
              <Checkbox checked={s[key] !== false} disabled={busy === key} onCheckedChange={(v) => flip(key, !!v, label)}
                        className="mt-0.5" data-testid={`hub-cc-pdf-${key.replace("cc_pdf_", "")}`} />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-slate-900">{label}</span>
                <span className="block text-[11.5px] leading-snug text-slate-500">{sub}</span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** How many company checks are left before the credits need topping up. */
export function CompanyCreditsCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/hub/company-check/credits").then((r) => setD(r.data)).catch(() => setD(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!d || !d.enabled) return null;

  const left = d.credits_remaining;
  const known = typeof left === "number";
  const out = known && left <= 0;
  const status = d.paused ? ["wait", "Switched off"] : !known ? ["slate", "Not read yet"] : out ? ["act", "Out of credits"] : d.low ? ["wait", "Running low"] : ["green", "Healthy"];

  const recheck = async () => {
    setBusy(true);
    try { const r = await api.post("/hub/company-check/credits/refresh"); setD(r.data); toast.success("Balance re-checked"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't read the balance just now"); }
    finally { setBusy(false); }
  };

  return (
    <Card label="Company check credits" Icon={Gauge} tone={out ? "act" : d.low || d.paused ? "wait" : "plain"} testId="hub-credits-card"
          right={<Pill tone={status[0]} testId="hub-credits-status">{status[1]}</Pill>}>
      <MasterSwitch d={d} onChange={setD} />
      <Parts d={d} onChange={setD} />
      <PdfAccess />
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[30px] font-extrabold leading-none tracking-tight text-slate-900 tabular-nums" data-testid="hub-credits-left">
            {known ? left.toLocaleString("en-GB") : "—"}<span className="ml-1.5 text-[13px] font-semibold text-slate-500">credits left</span>
          </div>
          <div className="mt-1.5 text-[12.5px] text-slate-500" data-testid="hub-credits-checks-left">
            {known ? `About ${d.checks_left} full check${d.checks_left === 1 ? "" : "s"} at ~${d.per_check} credits each` : "Runs a check or re-check to read the balance"}
          </div>
        </div>
      </div>
      {known && <Meter left={left} peak={d.peak} low={d.low_at} />}
      <div className="mt-5 grid grid-cols-3 gap-4">
        <Figure label="Checks this month" value={d.month?.checks || 0} testId="hub-credits-month" />
        <Figure label="Credits this month" value={(d.month?.credits || 0).toLocaleString("en-GB")} testId="hub-credits-month-credits" />
        <Figure label="Waiting to run" value={d.waiting || 0} tone={d.waiting ? "wait" : "slate"} testId="hub-credits-waiting" />
      </div>
      <div className="mt-5 flex flex-wrap items-center gap-2">
        <button type="button" onClick={recheck} disabled={busy || d.paused} data-testid="hub-credits-refresh"
                className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-slate-200 bg-white px-3 text-[12px] font-semibold text-slate-700 transition-colors hover:border-slate-300 disabled:opacity-60">
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />} Re-check balance
        </button>
        <a href={TOP_UP_URL} target="_blank" rel="noreferrer" data-testid="hub-credits-topup"
           className={`inline-flex h-9 items-center gap-1.5 rounded-[10px] px-3 text-[12px] font-semibold transition-colors ${d.low || out ? "bg-[#0055FF] text-white hover:bg-[#0044CC]" : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"}`}>
          Top up credits <ExternalLink className="size-3" />
        </a>
      </div>
      <Note>
        Warns under {d.low_at} credits and emails you once when it drops below — a top-up re-arms it.
        {d.checked_at ? ` Last read ${ukDayTime(d.checked_at, "")}.` : ""} Re-checking the balance uses 1 credit.
      </Note>
    </Card>
  );
}

export default CompanyCreditsCard;
